<template>
  <div class="key-areas-pressure">
    <div class="pressure-header">
      <div class="tabs-container">
        <div 
          v-for="tab in tabList" 
          :key="tab.key" 
          class="tab-item"
          :class="{ active: activeTab === tab.key }"
          @click="handleTabChange(tab.key)"
        >
          <span class="tab-label">{{ tab.title }}</span>
          <div class="tab-indicator"></div>
        </div>
      </div>
    </div>
    
    <div class="pressure-content">
      <div class="pressure-table-header">
        <div class="header-cell">{{ $t('管网名称') }}</div>
        <div class="header-cell">{{ $t('实时压力') }}</div>
        <div class="header-cell">{{ $t('实时流量') }}</div>
        <div class="header-cell">{{ $t('状态') }}</div>
      </div>
      
      <div class="pressure-table-body">
        <vue-seamless-scroll 
          :data="filteredData" 
          class="data-scroll"
          :class-option="scrollOption"
        >
          <div class="data-list">
            <div 
              v-for="(item, index) in filteredData" 
              :key="index" 
              class="data-row"
              :class="{ 'abnormal': isAbnormal(item) }"
            >
              <div class="data-cell name-cell">
                <div class="pipe-icon">
                  <svg-icon :name="getIconName()" class-name="pipe-svg" />
                </div>
                <span class="pipe-name">{{ item.name }}</span>
              </div>
              <div class="data-cell pressure-cell">
                <span class="pressure-value">{{ item.ssyl }}</span>
                <span class="pressure-unit">MPa</span>
              </div>
              <div class="data-cell flow-cell">
                <span class="flow-value">{{ item.ssll }}</span>
                <span class="flow-unit">m³/h</span>
              </div>
              <div class="data-cell status-cell">
                <div class="status-indicator" :class="getStatusClass(item)">
                  <span class="status-text">{{ getStatusText(item) }}</span>
                </div>
              </div>
            </div>
          </div>
        </vue-seamless-scroll>
      </div>
    </div>
  </div>
</template>

<script>
import vueSeamlessScroll from 'vue-seamless-scroll'
import "./icon/main-pipe.svg"
import "./icon/branch-pipe.svg"

export default {
  name: 'KeyAreasAndAbnormalPressure',
  components: {
    vueSeamlessScroll
  },
  props: {
    title: {
      type: String,
      default() {
        return this.$t('重点部位压力')
      }
    }
  },
  data() {
    return {
      activeTab: 0,
      tabList: [
        { key: 0, title: this.$t('主管道') },
        { key: 1, title: this.$t('支管道') }
      ],
      allData: [
        // 主管道数据
        [
          { name: '主城区主管网', ssyl: '2.45', ssll: '1250', status: 'normal' },
          { name: '工业园区主管网', ssyl: '2.38', ssll: '980', status: 'normal' },
          { name: '新城区主管网', ssyl: '2.52', ssll: '1100', status: 'normal' },
          { name: '老城区主管网', ssyl: '2.41', ssll: '850', status: 'warning' },
          { name: '开发区主管网', ssyl: '2.35', ssll: '920', status: 'normal' },
          { name: '高新区主管网', ssyl: '2.48', ssll: '1050', status: 'normal' },
          { name: '滨江区主管网', ssyl: '2.44', ssll: '1180', status: 'normal' }
        ],
        // 支管道数据
        [
          { name: '一号支管网', ssyl: '2.3', ssll: '234', status: 'normal' },
          { name: '二号支管网', ssyl: '2.4', ssll: '333', status: 'normal' },
          { name: '三号支管网', ssyl: '2.1', ssll: '321', status: 'warning' },
          { name: '四号支管网', ssyl: '2.4', ssll: '235', status: 'normal' },
          { name: '五号支管网', ssyl: '1.8', ssll: '542', status: 'danger' },
          { name: '六号支管网', ssyl: '2.5', ssll: '445', status: 'normal' },
          { name: '七号支管网', ssyl: '2.3', ssll: '298', status: 'normal' },
          { name: '八号支管网', ssyl: '2.2', ssll: '367', status: 'warning' }
        ]
      ]
    }
  },
  computed: {
    filteredData() {
      return this.allData[this.activeTab] || []
    },
    scrollOption() {
      return {
        step: 0.3,
        limitMoveNum: 4,
        hoverStop: true,
        direction: 1,
        openWatch: true,
        singleHeight: 0,
        waitTime: 1000
      }
    }
  },
  methods: {
    handleTabChange(key) {
      this.activeTab = key
    },
    isAbnormal(item) {
      return item.status === 'warning' || item.status === 'danger'
    },
    getStatusClass(item) {
      return {
        'status-normal': item.status === 'normal',
        'status-warning': item.status === 'warning',
        'status-danger': item.status === 'danger'
      }
    },
    getStatusText(item) {
      const statusMap = {
        normal: this.$t('正常'),
        warning: this.$t('警戒'),
        danger: this.$t('异常')
      }
      return statusMap[item.status] || this.$t('未知')
    },
    getIconName() {
      return this.activeTab === 0 ? 'main-pipe' : 'branch-pipe'
    }
  }
}
</script>

<style lang="less" scoped>
.key-areas-pressure {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(0, 20, 40, 0.3);
  border: 1px solid rgba(0, 150, 255, 0.2);
  border-radius: 8px;
  overflow: hidden;
}

.pressure-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 150, 255, 0.2);
  background: rgba(0, 30, 60, 0.4);
}

.tabs-container {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.tab-item {
  position: relative;
  cursor: pointer;
  padding: 6px 12px;
  transition: all 0.3s ease;
  
  .tab-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    transition: all 0.3s ease;
  }
  
  .tab-indicator {
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #00d4ff, #0096ff);
    transition: all 0.3s ease;
  }
  
  &.active {
    .tab-label {
      color: #00d4ff;
      text-shadow: 0 0 8px rgba(0, 212, 255, 0.5);
    }
    
    .tab-indicator {
      width: 100%;
    }
  }
  
  &:hover:not(.active) {
    .tab-label {
      color: rgba(255, 255, 255, 0.9);
    }
  }
}

.pressure-content {
  flex: 1;
  padding: 12px;
  overflow: hidden;
}

.pressure-table-header {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 0.8fr;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(0, 40, 80, 0.3);
  border-radius: 4px;
  margin-bottom: 8px;
  
  .header-cell {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    text-align: center;
  }
}

.pressure-table-body {
  height: calc(100% - 40px);
  overflow: hidden;
}

.data-scroll {
  height: 100%;
}

.data-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.data-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 0.8fr;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 150, 255, 0.1);
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 150, 255, 0.1);
    border-color: rgba(0, 150, 255, 0.3);
    transform: translateX(2px);
  }
  
  &.abnormal {
    background: rgba(255, 100, 0, 0.1);
    border-color: rgba(255, 100, 0, 0.3);
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.data-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.name-cell {
  justify-content: flex-start;
  gap: 8px;
  
  .pipe-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 150, 255, 0.1);
    border-radius: 4px;
    
    .pipe-svg {
      width: 16px;
      height: 16px;
      fill: #00d4ff;
    }
  }
  
  .pipe-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.pressure-cell, .flow-cell {
  flex-direction: column;
  align-items: center;
  gap: 2px;
  
  .pressure-value, .flow-value {
    font-family: 'ds-digital-bold';
    font-size: 14px;
    color: #00d4ff;
    font-weight: bold;
  }
  
  .pressure-unit, .flow-unit {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
  }
}

.status-cell {
  .status-indicator {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    
    &.status-normal {
      background: rgba(0, 200, 83, 0.2);
      color: #00c853;
      border: 1px solid rgba(0, 200, 83, 0.3);
    }
    
    &.status-warning {
      background: rgba(255, 152, 0, 0.2);
      color: #ff9800;
      border: 1px solid rgba(255, 152, 0, 0.3);
      animation: blink 1.5s infinite;
    }
    
    &.status-danger {
      background: rgba(244, 67, 54, 0.2);
      color: #f44336;
      border: 1px solid rgba(244, 67, 54, 0.3);
      animation: blink 1s infinite;
    }
  }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

// 响应式设计
@media (max-width: 768px) {
  .pressure-table-header {
    grid-template-columns: 2fr 1fr 1fr 0.8fr;
    gap: 4px;
    padding: 6px 8px;
    
    .header-cell {
      font-size: 11px;
    }
  }
  
  .data-row {
    grid-template-columns: 2fr 1fr 1fr 0.8fr;
    gap: 4px;
    padding: 8px;
    
    .data-cell {
      font-size: 12px;
    }
    
    .name-cell {
      .pipe-icon {
        width: 20px;
        height: 20px;
        
        .pipe-svg {
          width: 14px;
          height: 14px;
        }
      }
    }
    
    .pressure-value, .flow-value {
      font-size: 13px;
    }
    
    .status-indicator {
      padding: 3px 6px;
      font-size: 10px;
    }
  }
}

@media (max-width: 480px) {
  .tabs-container {
    gap: 12px;
  }
  
  .tab-item {
    padding: 4px 8px;
    
    .tab-label {
      font-size: 12px;
    }
  }
  
  .pressure-content {
    padding: 8px;
  }
  
  .pressure-table-header {
    grid-template-columns: 1.8fr 1fr 1fr 0.7fr;
    gap: 2px;
  }
  
  .data-row {
    grid-template-columns: 1.8fr 1fr 1fr 0.7fr;
    gap: 2px;
    padding: 6px;
  }
}
</style>